import { defineStore } from "pinia";
import { computed } from "vue";
import { makeLogger } from "@/shared/consola";
import { t } from "@/i18n";
// import { DownloadFileIcon, PdfIcon, TextFileIcon, VideoIcon } from "@easy/ui/icons";
// import { PLAYER_FILE_TYPES } from "@/apps/player/services/players.service";
import { useOnboarding } from "@/shared/projects/onboarding/useOnboarding";
import SvgPlayerLayout from "@/apps/player/project/onboarding/svg-previews/SvgPlayerLayout.vue";
import { useActions, useMutations, useState } from "@/store/vuex-composition-mappers";
import {
    PLAYER_FILE_TYPES,
    PLAYER_HEADER_SIDES,
    PLAYER_HEADER_TYPES,
} from "@/apps/player/services/players.service";
import { playerCartService } from "@/apps/player/services/player-cart.service";
import { DownloadFileIcon, PdfIcon, TextFileIcon, VideoIcon } from "@easy/ui/icons";
import { mapThemeToPlayerColors } from "@/shared/projects/prepare-theme";

const STORE_NAME = "player-creator";
const logger = makeLogger("🍍" + STORE_NAME);

export const usePlayerCreatorStore = defineStore(STORE_NAME, () => {
    logger.trace("👋hello");

    const { player } = useState("player", ["player"]);
    const { addFile, addHeader } = useMutations("player", ["addFile", "addHeader"]);
    // const { fillWithExample } = useActions("player", ["fillWithExample"]);
    const { saveSnapshot } = useActions("player/projects", ["saveSnapshot"]);
    const { showAlert } = useMutations(["showAlert"]);

    const {
        isCreatorShown,
        onboardState,
        previewCssVars,
        autoOpenCreator,
        toggleCreatorShown,
        updateProductInfo,
        validate,
    } = useOnboarding({
        // autoOpenRules: (files) => files.filter((t) => !t._deleted).length === 0,
        initialValues: {
            //showProductPickerOnly: true,
            hidePrompt: true, // for now (EAS-2196)
            template: "empty",
        },
    });

    onboardState.promptDescription = "use:(empty)"; // for now (EAS-2196)
    onboardState.companyName = "";
    onboardState.website = "";

    const templates = computed(() => [
        {
            value: t("faq.templates.empty"),
            id: "empty",
        },
        {
            value: t("player.files.video"),
            icon: VideoIcon,
            id: PLAYER_FILE_TYPES.video,
        },
        {
            value: t("player.files.text"),
            icon: TextFileIcon,
            id: PLAYER_FILE_TYPES.text,
        },
        {
            value: t("player.files.file"),
            icon: DownloadFileIcon,
            id: PLAYER_FILE_TYPES.file,
        },
        {
            value: "PDF",
            icon: PdfIcon,
            id: PLAYER_FILE_TYPES.pdf,
        },
    ]);

    const layouts = computed(() => []);

    const previewComponent = computed(() => SvgPlayerLayout);

    return {
        templates,
        onboardState,
        isCreatorShown,
        layouts,
        previewCssVars,
        previewComponent,
        links: computed(() => ({
            video: "",
            docs: "",
        })),
        autoOpenCreator,
        onboardingAction(resolveResponse) {
            return async () => {
                if (!validate()) {
                    return;
                }
                try {
                    onboardState.isLoading = true;
                    let result = { courseName: "", lessons: [] };
                    if (!(onboardState.mode === "by-product" && onboardState.productSelectLater)) {
                        const { result: callResult } = await resolveResponse();
                        result = callResult;
                    }
                    const { theme, layout, companyName, website, logo, productDetails } =
                        onboardState;
                    logger.debug("about to onboardingAction", {
                        result,
                        theme,
                        layout,
                    });
                    const { courseName, lessons } = result;
                    for (const {
                        title,
                        description,
                        info,
                        type,
                        videoSrc,
                        src = "",
                        text,
                    } of lessons) {
                        addFile({
                            name: title,
                            description,
                            info,
                            type,
                            videoSrc,
                            src,
                            text,
                        });
                    }
                    for (const [key, value] of Object.entries(mapThemeToPlayerColors(theme))) {
                        player.value[key] = value;
                    }
                    if (productDetails?.id) {
                        try {
                            // connect with easycart:
                            await playerCartService.connectToCart({
                                playerUuid: player.value.uuid,
                                ecProductId: productDetails.id,
                            });
                            player.value.ecProductIds = [productDetails.id];
                        } catch (error) {
                            console.error(error);
                        }
                    }
                    const { link, squareLink } = logo || {};
                    if (link || squareLink) {
                        player.value.logo = link || squareLink;
                        player.value.pageIcon = squareLink || link;
                    }
                    player.value.pageTitle = courseName;
                    player.value.footerText = companyName;
                    addHeader({
                        name: companyName,
                        type: website ? PLAYER_HEADER_TYPES.button : PLAYER_HEADER_TYPES.text,
                        side: PLAYER_HEADER_SIDES.right,
                        href: website,
                    });
                    showAlert({
                        type: "success",
                        title: t("shared.creator.generation_success"),
                    });
                    await saveSnapshot();
                    toggleCreatorShown(false);
                } catch (e) {
                    logger.error(e);
                } finally {
                    onboardState.isLoading = false;
                }
            };
        },
        toggleCreatorShown,
        updateProductInfo,
        validate,
    };
});
